import type {DraggableAttributes} from '@dnd-kit/core';
import type {SyntheticListenerMap} from '@dnd-kit/core/dist/hooks/utilities';
import {Flag} from 'platform/components';
import {Box, HStack, Icon, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {isEmpty, join} from 'ramda';

import {useCatalogue} from '../../../../../hooks/useCatalogue';
import i18n from '../../../../../i18n/i18n';
import {RelativeFilterResponseBody} from '../../../../../types/api/api';
import {getPresetDisplayName} from '../utils/getPresetDisplayName';
import {getPresetTags} from '../utils/getPresetTags';

interface PresetListItemProps {
  preset: RelativeFilterResponseBody;
  isSelected: boolean;
  isSortable: boolean;
  dragHandleAttributes?: DraggableAttributes;
  dragHandleListeners?: SyntheticListenerMap;
  onSelect: (presetId: string) => void;
  'data-testid'?: string;
}

export function PresetListItem(props: PresetListItemProps) {
  const {preset, isSelected, isSortable, dragHandleAttributes, dragHandleListeners, onSelect} =
    props;
  const {getFeatureLabel, hasFeatureTranslation} = useCatalogue();
  const tags = getPresetTags(preset.config, {
    getFeatureLabel,
    isKnownFeature: hasFeatureTranslation,
  });

  return (
    <RowButton
      type="button"
      $isSelected={isSelected}
      onClick={() => onSelect(preset.id)}
      data-testid={props['data-testid']}
    >
      <HStack spacing={2} align="center">
        <DragHandleSlot>
          {isSortable ? (
            <DragHandle
              {...dragHandleAttributes}
              {...dragHandleListeners}
              onClick={(event) => event.stopPropagation()}
            >
              <Icon value="action/drag_indicator" color="palettes.neutral.60.100" size={4} />
            </DragHandle>
          ) : null}
        </DragHandleSlot>
        <Box flexGrow={1} minWidth={0}>
          <VStack spacing={1}>
            <HStack spacing={1} align="center" wrap>
              <Text size="small" alternative>
                {getPresetDisplayName(preset)}
              </Text>
              {preset.default ? (
                <Flag
                  label={i18n.t('entity.filterForm.savedFilters.defaultBadge')}
                  size="small"
                  colorScheme="blue"
                  isSubtle
                />
              ) : null}
            </HStack>
            {!isEmpty(tags) ? (
              <Text size="xSmall" color="secondary" noWrap>
                {join(', ', tags)}
              </Text>
            ) : null}
          </VStack>
        </Box>
      </HStack>
    </RowButton>
  );
}

const RowButton = styled.button<{$isSelected: boolean}>`
  display: block;
  width: 100%;
  padding: ${({theme}) => theme.getSize(2)};
  border: none;
  text-align: left;
  cursor: pointer;
  background-color: ${({theme, $isSelected}) =>
    $isSelected ? theme.colors.palettes.blue[10][100] : theme.colors.palettes.white[10][100]};
  color: inherit;
  border-radius: ${({theme}) => theme.radii.small};

  &:hover,
  &:focus-visible {
    background-color: ${({theme}) => theme.colors.palettes.blue[10][100]};
    outline: none;
  }
`;

const DragHandleSlot = styled.div`
  width: ${({theme}) => theme.getSize(4)};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  cursor: grab;
  opacity: 0;

  ${RowButton}:hover &,
  ${RowButton}:focus-within & {
    opacity: 1;
  }
`;
